import classNames from 'classnames';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Reason } from '@/entities/reason';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import Pagination from '@/shared/components/shared/Pagination';
import { useMounted } from '@/shared/hooks/use-mounted';
import { isSamePath } from '@/shared/hooks/use-open-popup-from-get-params';
import { useOverflowing } from '@/shared/hooks/use-overflowing';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { Theme } from '@/shared/lib/types';
import Button from '../../shared/Button';
import Image from '../../shared/Image';
import Link from '../../shared/Link';
import Responsive from '../../shared/Responsive';

export type MissionSectionProps = {
    title: string;
    subtitle: string;
    link?: {
        name: string;
        href: string;
    } | null;
    cards: Reason[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    missionSectionProps: MissionSectionProps;
    'data-theme'?: Theme;
};

function MissionSection({ missionSectionProps, ...props }: Props) {
    const SECTION_ID = props.id || 'mission';

    const sliderRef = useRef<SwiperRef>();
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const numSlides = missionSectionProps.cards.length;
    const tpTitle = useTypograf(missionSectionProps.title);
    const tpSubtitle = useTypograf(missionSectionProps.subtitle);
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';

    const slider = useRef<HTMLDivElement | null>(null);
    const isInitialized = useOverflowing(slider);
    const isMounted = useMounted();
    const [loop, setLoop] = useState(false);

    useEffect(() => {
        setLoop(true);
    }, []);

    useEffect(() => {
        sliderRef.current?.swiper.update();
    }, [loop]);
    return (
        numSlides > 0 && (
            <section
                {...props}
                className={classNames('mission__section js-header-theme-trigger', props.className)}
                id={SECTION_ID}
                data-theme={props['data-theme'] ?? 'dark'}
            >
                <div className="mission__wrapper">
                    <div className="mission-top wrapper">
                        <div className="mission-title h3">{tpTitle}</div>
                        <div className="mission-description-block">
                            <div className="mission-description text-s">{tpSubtitle}</div>
                            {missionSectionProps.link && missionSectionProps.link.href && (
                                <Button
                                    tag={Link}
                                    href={missionSectionProps.link.href}
                                    target={isSamePath(missionSectionProps.link.href) ? undefined : '_blank'}
                                    rel={isSamePath(missionSectionProps.link.href) ? undefined : 'noreferrer nofollow'}
                                    className="mission-section-btn"
                                    variant="outline-dark"
                                >
                                    {missionSectionProps.link.name ?? 'Подать заявку'}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {isInitialized && !isDesktop && isMounted ? (
                    <div className="mission-bottom">
                        <Swiper
                            className={classNames('mission-slider', { 'is-enabled': isInitialized })}
                            ref={sliderRef as MutableRefObject<SwiperRef>}
                            modules={[Navigation]}
                            speed={650}
                            slidesPerView={1}
                            loop={loop}
                            breakpoints={{
                                320: {
                                    spaceBetween: 8,
                                    slidesPerView: 1.16,
                                    centeredSlides: true,
                                },
                                767: {
                                    slidesPerView: 2.1,
                                    spaceBetween: 12,
                                    slidesOffsetBefore: 12,
                                },
                                1201: {
                                    slidesPerView: 3,
                                    spaceBetween: 20,
                                    slidesOffsetBefore: 70,
                                },
                            }}
                            onSlideChange={(swiper) => {
                                const newRealIndex = swiper.realIndex;
                                setLastRealIndex(currentRealIndex);
                                setCurrentRealIndex(newRealIndex);
                            }}
                        >
                            {missionSectionProps.cards.map((card, i) => (
                                <SwiperSlide key={i}>
                                    <div className="mission-card__item mission-card__item-slide" key={i}>
                                        <div className="mission-card__item-wrapper">
                                            <div className="mission-card__item-img">
                                                <Responsive className="mission-card__img-wrapper">
                                                    {card.img?.src && (
                                                        <Image
                                                            className="mission-card__item-img"
                                                            src={card.img.src}
                                                            alt={card.img.alt || ''}
                                                            title={card.img.title}
                                                            width={card.img.width}
                                                            height={card.img.height}
                                                            sizes="(max-width: 769px) 100vw, (max-width: 1199px) 50vw, 30vw"
                                                        />
                                                    )}
                                                </Responsive>
                                            </div>
                                            <div className="mission-card__item-content">
                                                <div className="text-xl mission-card__item-title ">{card.text}</div>
                                                {card.detail && (
                                                    <div className="mission-card__item-descr text-s">{card.detail}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {isInitialized && (
                            <Pagination
                                numSlides={numSlides}
                                currentRealIndex={currentRealIndex}
                                lastRealIndex={lastRealIndex}
                                prevElClass="slider-prev-btn"
                                nextElClass="slider-next-btn"
                                wrapperClass="mission-pagination-wrapper pagination-wrapper wrapper"
                                onPrevClick={() => {
                                    sliderRef.current?.swiper.slidePrev();
                                }}
                                onNextClick={() => {
                                    sliderRef.current?.swiper.slideNext();
                                }}
                            />
                        )}
                    </div>
                ) : (
                    <div className="mission-bottom wrapper swiper__wrapper">
                        <div className="mission-cards__list list-unstyled" ref={slider}>
                            {missionSectionProps.cards.map((card, i) => (
                                <div className="mission-card__item" key={i}>
                                    <div className="mission-card__item-wrapper">
                                        <div className="mission-card__item-img">
                                            <Responsive className="mission-card__img-wrapper">
                                                {card.img?.src && (
                                                    <Image
                                                        className="mission-card__item-img"
                                                        src={card.img.src}
                                                        alt={card.img.alt || ''}
                                                        title={card.img.title}
                                                        width={card.img.width}
                                                        height={card.img.height}
                                                        sizes="(max-width: 769px) 100vw, (max-width: 1199px) 50vw, 30vw"
                                                    />
                                                )}
                                            </Responsive>
                                        </div>
                                        <div className="mission-card__item-content">
                                            <div className="text-xl mission-card__item-title ">{card.text}</div>
                                            {card.detail && (
                                                <div className="mission-card__item-descr text-s">{card.detail}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        )
    );
}

export default MissionSection;
